import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { conferenceFor, departuresFor, INDEPENDENT } from '../../lib/sportAffiliation.js'
import { api } from '../../lib/api.js'
import styles from './SchoolDrawer.module.css'
import OutreachPicker from './OutreachPicker'

/**
 * ONE SCHOOL — every sport, the conference that crowns it, and who to call.
 *
 * Brian: "we need to know that certain sports compete in different conferences
 * like app state competes in multiple sports - and then i should be able to
 * click on a school so it can bring me to all their coaches with contact
 * information which we will need to get if we dont have it."
 *
 * This is the last link in the chain the whole project has been building:
 *
 *   championship → SCHOOL → sport → CONFERENCE that crowns it → COACH → outreach
 *
 * Every earlier piece answers one hop. This panel is where they meet, and it is
 * the first screen that can answer "they just won — who do I ring?".
 *
 * ⚠️ IT REPORTS WHAT IS MISSING AS LOUDLY AS WHAT IS PRESENT. Measured against
 * the live backend today, a college school resolves an OWNED crest, brand
 * colours and a mascot — and returns NO COACH AT ALL. That is not a rendering
 * gap to hide behind a spinner; it is the single thing standing between a
 * crowned champion and a phone call, and it belongs on screen with the reason.
 */

const SPORTS = [
  'Football', 'Basketball', 'Baseball', 'Softball', 'Soccer', 'Volleyball',
  'Ice Hockey', 'Lacrosse', 'Wrestling', 'Track & Field', 'Cross Country',
  'Swimming & Diving', 'Golf', 'Tennis', 'Water Polo', 'Field Hockey',
]

export default function SchoolDrawer({ school, onClose }) {
  const [coach, setCoach] = useState({ status: 'idle', data: null, error: '' })
  const [sport, setSport] = useState('')

  /* Every sport this school plays, with the body that actually crowns it.
   * The DEPARTURES are listed first and marked, because they are the rows a
   * flat school→conference model gets wrong — and the rows a rep is most
   * likely to file against the wrong governing body. */
  const rows = useMemo(() => {
    if (!school?.name) return []
    const dep = new Map(departuresFor(school.name).map((d) => [d.sport, d.conference]))
    const primary = conferenceFor(school.name)
    const out = []
    for (const [s, conf] of dep) out.push({ sport: s, conference: conf, departure: true })
    for (const s of SPORTS) {
      if ([...dep.keys()].some((k) => k.toLowerCase().includes(s.toLowerCase())
        || s.toLowerCase().includes(k.toLowerCase()))) continue
      out.push({ sport: s, conference: primary, departure: false })
    }
    return out
  }, [school])

  /* A read that failed for a transient reason is worth ASKING AGAIN — bumping
   * this re-runs the effect, which is the whole "Try again" button. */
  const [attempt, setAttempt] = useState(0)
  const runLookup = () => setAttempt((n) => n + 1)
  /* The school the rep PICKED out of an ambiguous list, if they picked one.
   * Held separately from `school` so the drawer still knows what it was opened
   * on, and so switching sport or school clears it rather than carrying a
   * previous school's disambiguation onto a different record. */
  const [pick, setPick] = useState('')
  useEffect(() => { setPick('') }, [school])

  useEffect(() => {
    if (!school?.name || !sport) { setCoach({ status: 'idle', data: null, error: '' }); return }
    let alive = true
    setCoach({ status: 'loading', data: null, error: '' })
    /* The drawer is already open on ONE company, so pass its kind through —
     * a school named "Wake Forest" must not re-resolve to a different
     * institution than the one being looked at. */
    api.coach({ school: pick || school.name, sport, segment: school.orgType || school.segment || '' })
      .then((d) => { if (alive) setCoach({ status: 'done', data: d, error: '' }) })
      .catch((e) => { if (alive) setCoach({ status: 'done', data: null, error: e?.message || 'Lookup failed' }) })
    return () => { alive = false }
  }, [school, sport, attempt, pick])

  if (!school) return null

  const c = coach.data
  const mark = c?.mark
  const needs = c?.needs_review

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <aside className={styles.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div>
            <p className={styles.eyebrow}>{school.state || 'School'}</p>
            <h2 className={styles.title}>{school.name}</h2>
            {/* ⚠️ THE WAY OUT TO THE REAL RECORD. Brian: "possibly take me to
                the hubspot thats tied to this name."
                HubSpot has no stable deep-link from a NAME — only from an
                object id, which a roster entry does not carry — so this opens
                HubSpot's own search for the school rather than pretending to
                know an id it does not have. A search that lands is worth more
                than a deep link that 404s. */}
            <a className={styles.hubspot}
              href={`https://app.hubspot.com/contacts/51263144/objects/0-2/views/all/list?query=${encodeURIComponent(school.name)}`}
              target="_blank" rel="noopener noreferrer">
              Find “{school.name}” in HubSpot ↗
            </a>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* THE BRAND KIT — what we already own for this school, which is what an
            artwork build needs on the day they win. */}
        {mark && (
          <div className={styles.brand}>
            {mark.logoUrl && <img className={styles.crest} src={mark.logoUrl} alt={`${school.name} crest`} />}
            <div className={styles.brandMeta}>
              {mark.mascot && <span className={styles.mascot}>{mark.mascot}</span>}
              <span className={styles.swatches}>
                {(mark.colors || []).map((hex) => (
                  <span key={hex} className={styles.swatch} style={{ background: hex }} title={hex} />
                ))}
              </span>
              {/* OWNED means hosted by us — never a link to their server. */}
              {mark.source === 'owned' && <span className={styles.owned}>owned crest</span>}
            </div>
          </div>
        )}

        {/* ⚠️ EVERY SPORT, AND THE BODY THAT ACTUALLY CROWNS IT. This is the
            answer to "App State competes in multiple conferences" — a school
            does not have ONE conference, it has one per sport. */}
        <div className={styles.sports}>
          <span className={styles.sectionHead}>Where each sport is crowned</span>
          {rows.map((r) => (
            <button key={r.sport} type="button" className={styles.sportRow}
              data-on={sport === r.sport ? '1' : '0'} data-departure={r.departure ? '1' : '0'}
              onClick={() => setSport(r.sport)}>
              <span className={styles.sportName}>{r.sport}</span>
              <span className={styles.sportConf}>
                {r.conference === INDEPENDENT ? 'Independent — no conference title' : (r.conference || 'Not sourced')}
              </span>
              {/* The rows a flat model gets wrong, marked so they stand out. */}
              {r.departure && <span className={styles.depFlag}>different conference</span>}
            </button>
          ))}
        </div>

        {/* THE COACH — the last hop, and today the broken one. */}
        <div className={styles.coach}>
          <span className={styles.sectionHead}>
            {sport ? `Who to call — ${sport}` : 'Pick a sport to look up its coach'}
          </span>

          {coach.status === 'loading' && <p className={styles.muted}>Looking up the coaching contact…</p>}

          {coach.status === 'done' && c?.coach && (
            <div className={styles.contact}>
              <b>{c.coach.name}</b>
              {c.coach.title && <span>{c.coach.title}</span>}
              {c.coach.email && <a href={`mailto:${c.coach.email}`}>{c.coach.email}</a>}
              {c.coach.phone && <a href={`tel:${c.coach.phone}`}>{c.coach.phone}</a>}
            </div>
          )}

          {/* ⚠️ ONE SPORT, TWO HEAD COACHES — SAY SO INSTEAD OF PICKING QUIETLY.
              A college fields a mens AND a womens head coach for the same sport,
              so an ungendered lookup has a real choice to make. It used to take
              the first row silently. Naming the others is what stops a rep
              emailing the wrong team; the alternates carry their own contact
              details so choosing costs nothing. */}
          {coach.status === 'done' && c?.coach?.alternates?.length > 0 && (
            <div className={styles.alternates}>
              <span className={styles.altHead}>
                {c.coach.confidence < 1
                  ? `More than one head coach is on file for ${sport} — check which team you mean:`
                  : 'Also on file for this sport:'}
              </span>
              {c.coach.alternates.map((a, i) => (
                <div className={styles.contact} key={`${a.name}-${i}`}>
                  <b>{a.name}</b>
                  {a.title && <span>{a.title}</span>}
                  {a.email && <a href={`mailto:${a.email}`}>{a.email}</a>}
                  {a.phone && <a href={`tel:${a.phone}`}>{a.phone}</a>}
                </div>
              ))}
            </div>
          )}

          {/* ⚠️ THE HEAD COACH IS OFTEN NOT THE DECISION MAKER.
              Brian: "Head coaches a lot of the time are not decision makers,
              they might have final say but they aren't doing championship ring
              designs, they just like to be included. So assistants are also
              very important as they get reached out to especially in college."

              The AD, the administrators and the assistants were all loaded into
              HubSpot and then filtered out of this panel by an `sr_role =
              'Head Coach'` query. They are the people who approve and specify a
              ring order, so they belong on the screen the rep actually opens.
              Grouped rather than ranked into one list: the AD signs, the
              assistant answers the phone, the head coach wants to be included —
              a single "best contact" would be wrong for two of the three. */}
          {coach.status === 'done' && c?.committee && (
            <div className={styles.committee}>
              {[
                ['Athletic director', c.committee.athleticDirectors, 'signs off'],
                ['Administration', c.committee.administrators, 'president · principal · AD office'],
                [sport ? `Assistants — ${sport}` : 'Assistants', c.committee.assistants, 'usually who actually answers'],
                /* ⚠️ STAFF IS RENDERED, and it is not a nice-to-have.
                   Brian: "it has dropped some faculty and admin off lists."
                   `mapRole` was written for K-12, so a college PRESIDENT,
                   chancellor or dean matched nothing and fell into Staff. The
                   classifier is fixed for future loads, but the ~226k contacts
                   already in HubSpot still carry Staff — so hiding this group
                   keeps the most senior person at the school invisible on the
                   one screen a rep opens. */
                ['Faculty & staff', c.committee.staff, 'presidents, deans, trainers, SIDs'],
                ['Other head coaches', c.committee.otherHeadCoaches, 'when this sport has nobody on file'],
              ].filter(([, list]) => list && list.length).map(([label, list, why]) => (
                <div className={styles.group} key={label}>
                  <span className={styles.groupHead}>
                    {label} <em>· {why}</em>
                  </span>
                  {list.slice(0, 8).map((p, i) => (
                    <div className={styles.contact} key={`${p.name}-${i}`}>
                      <b>{p.name}</b>
                      {p.title && <span>{p.title}</span>}
                      {p.email && <a href={`mailto:${p.email}`}>{p.email}</a>}
                      {p.phone && <a href={`tel:${p.phone}`}>{p.phone}</a>}
                    </div>
                  ))}
                  {list.length > 8 && <span className={styles.muted}>+{list.length - 8} more on the record</span>}
                </div>
              ))}
            </div>
          )}

          {/* ⚠️ SURFACING PEOPLE IS ONLY HALF A FEATURE.
              Brian: "If you surface those people from the championship and have
              them viewable, what's the CTA for my team."

              Without this the rep reads a list of names and then opens Gmail.
              The picker turns the list into a staged, gated draft — and it is
              placed directly under the committee it selects from, because a CTA
              that lives anywhere else is a CTA that gets scrolled past. */}
          {coach.status === 'done' && (
            <OutreachPicker
              school={school}
              coach={c?.coach}
              committee={c?.committee}
              sport={sport}
              championshipId={school?.championshipId || school?.id || null}
            />
          )}

          {/* ⚠️ THE HONEST BLOCKER, WITH ITS REASON. Measured live: colleges
              resolve an owned crest and brand colours and return NO COACH. A
              spinner that resolves to nothing, or a blank panel, would hide the
              one fact that decides whether a crowned championship turns into a
              call. The backend's own `needs_review.reason` is printed verbatim
              rather than paraphrased.

              ⚠️ AND IT IS NO LONGER A DEAD END: a school can have no head coach
              for this sport and still have an AD and four assistants who can be
              called. `reachable` says which case this is. */}
          {coach.status === 'done' && !c?.coach && sport && (
            <div className={styles.blocked}>
              {/* ⚠️ "COULD NOT READ" IS NOT "NOBODY IS HERE", AND THIS PANEL SAID
                  THE SECOND FOR BOTH.

                  `findPeople` was called as `.catch(() => [])`, so a HubSpot
                  rate limit — routine, because the coach loader shares that
                  quota — arrived here as an empty roster and rendered as "No
                  coaching contact on file." Measured on Wofford College, which
                  holds 45 people.

                  A rep does not retry a confident "nobody is here". They
                  conclude the school was never loaded and ask for it again. So
                  the read failure gets its own heading, its own explanation and
                  a retry — it must never wear the empty-school wording. */}
              <b>{needs?.reason === 'people_lookup_failed'
                ? 'Could not read this school’s contacts.'
                : needs?.reason === 'ambiguous_school'
                ? 'That name belongs to more than one school.'
                : 'No coaching contact on file.'}</b>
              <span>
                {needs?.reason === 'people_lookup_failed'
                  ? (needs.note || 'HubSpot did not answer the contact search. This is not a school with nobody on it.')
                  : needs?.reason === 'ambiguous_school'
                  ? (needs.note || 'Pick the one you mean — nothing was guessed.')
                  : needs?.reason === 'school_not_found'
                  ? 'This school is not matched in HubSpot under that name. HubSpot stores formal names — “Appalachian State University Boone, North Carolina” — while the conference rosters use athletic names, so the two do not join.'
                  : needs?.reason === 'no_school_key'
                    ? 'The school record was found and carries an owned crest and brand colours, but it has no coaching contacts attached at all.'
                    : (coach.error || 'The lookup returned no contact.')}
              </span>
              {needs?.reason === 'people_lookup_failed' && (
                <button className={styles.retry} onClick={() => runLookup?.()}>Try again</button>
              )}

              {/* ⚠️ A REFUSAL A REP CANNOT ACT ON IS BARELY BETTER THAN A WRONG
                  ANSWER. The resolver now declines to guess between "Wake
                  Forest" the university and "Wake Forest" the high school —
                  correct, but on its own it just moves the dead end. So the
                  candidates it returns are BUTTONS: one click re-runs the
                  lookup against that exact school.

                  Each option shows its KIND and its contact count, because
                  those are the two things that tell them apart on sight — and
                  the count is the very signal that used to decide this silently
                  and wrongly. Shown, it is information; hidden, it was a bug. */}
              {needs?.reason === 'ambiguous_school' && (c?.candidates || []).length > 0 && (
                <div className={styles.candidates}>
                  {c.candidates.map((cand) => (
                    <button key={cand.id} type="button" className={styles.candidate}
                      onClick={() => setPick(cand.name)}>
                      <b>{cand.name}</b>
                      <span>{[cand.kind === 'high' ? 'high school'
                        : cand.kind === 'middle' ? 'middle school'
                        : cand.kind || 'kind not recorded',
                        `${cand.contacts} contact${cand.contacts === 1 ? '' : 's'}`].join(' · ')}</span>
                    </button>
                  ))}
                </div>
              )}
              <span className={styles.blockedWhy}>
                {needs?.reason === 'people_lookup_failed'
                  ? 'Nothing about this school has changed — only our ability to read it right now. The detail above is HubSpot’s own message.'
                  : needs?.reachable
                  ? `${needs.reachable} other people at this school ARE contactable — the athletic director and the assistants are listed above. The head coach is missing, not the school.`
                  : 'Until this is filled, a crowned champion in this sport cannot be turned into an outreach — the artwork can be built and there is nobody to send it to.'}
              </span>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  )
}
